<template>
  <v-dialog v-model="open" persistent max-width="640px">
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-map-marker-path</v-icon>
        <span class="text-h6">
          {{ $t('map_n_students', { count: students.length }) }}
        </span>
      </v-card-title>

      <v-card-subtitle class="pt-2">{{ $t('map_students_hint') }}</v-card-subtitle>

      <v-card-text>
        <v-btn-toggle v-model="pickUp" mandatory dense class="mb-4">
          <v-btn :value="true">
            <v-icon small class="me-1">mdi-weather-sunset-up</v-icon>
            {{ $t('pickup') }}
          </v-btn>
          <v-btn :value="false">
            <v-icon small class="me-1">mdi-weather-sunset-down</v-icon>
            {{ $t('drop_off') }}
          </v-btn>
        </v-btn-toggle>

        <v-select
          v-model="routeId"
          :items="matchingRoutes"
          item-text="name"
          item-value="id"
          :label="$t('route')"
          :loading="isLoading"
          :no-data-text="$t('no_routes_for_this_direction')"
          dense
          outlined
          @change="onRouteChange"
        ></v-select>

        <v-select
          v-model="stopId"
          :items="stopItems"
          item-text="label"
          item-value="stop_id"
          :label="$t('stop')"
          :disabled="!routeId"
          dense
          outlined
        ></v-select>

        <v-select
          v-model="tripId"
          :items="tripItems"
          item-text="label"
          item-value="id"
          :label="$t('trip')"
          :disabled="!stopId"
          :hint="tripHint"
          persistent-hint
          dense
          outlined
        ></v-select>

        <!-- a trip whose bus cannot seat everyone selected is worth saying up
             front rather than after a partial run -->
        <v-alert v-if="seatWarning" type="warning" dense text class="mt-3 mb-0">
          {{ seatWarning }}
        </v-alert>

        <!-- a new stop is something the family has to act on tomorrow morning;
             nothing used to tell them and they found out when the bus went past -->
        <v-divider class="my-4"></v-divider>
        <v-switch
          v-model="notify"
          :label="$t('notify_the_family')"
          :hint="$t('notify_the_family_hint')"
          persistent-hint
          dense
          inset
        ></v-switch>
        <v-textarea
          v-if="notify"
          v-model="reason"
          :label="$t('reason_optional')"
          rows="2"
          class="mt-3"
        ></v-textarea>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="isSaving" @click="clearMapping">
          {{ $t('unmap') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text :disabled="isSaving" @click="open = false">{{ $t('close') }}</v-btn>
        <v-btn
          color="primary"
          text
          :loading="isSaving"
          :disabled="!tripId"
          @click="save"
        >
          {{ $t('map_students') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/*
 * Puts a selection of students onto one stop and one trip.
 *
 * Until now only a parent could do this, from the app, so a school with two
 * hundred unmapped students had two hundred phone calls ahead of it. The route,
 * stop and trip lists come from one call and cascade, because a stop only makes
 * sense within a route and a trip only within the stops it calls at.
 */
export default {
  name: "MapStudentsDialog",
  props: {
    value: { type: Boolean, default: false },
    students: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      routes: [],
      pickUp: true,
      notify: false,
      reason: "",
      routeId: null,
      stopId: null,
      tripId: null,
    };
  },
  computed: {
    open: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    //morning routes carry pickups, afternoon routes carry drop-offs
    matchingRoutes() {
      return this.routes.filter((route) => route.is_morning === this.pickUp);
    },
    selectedRoute() {
      return this.routes.find((route) => route.id === this.routeId) || null;
    },
    stopItems() {
      if (!this.selectedRoute) return [];
      return this.selectedRoute.stops.map((stop) => ({
        ...stop,
        label: `${stop.order}. ${stop.name}`,
      }));
    },
    //only trips that actually call at the chosen stop
    tripItems() {
      if (!this.selectedRoute || !this.stopId) return [];
      return this.selectedRoute.trips
        .filter((trip) => (trip.stop_ids || []).indexOf(this.stopId) !== -1)
        .map((trip) => ({
          ...trip,
          label: trip.has_bus
            ? `${trip.name} — ${this.$t("n_seats_free", { count: trip.available_seats })}`
            : `${trip.name} — ${this.$t("no_bus_assigned")}`,
        }));
    },
    selectedTrip() {
      return this.tripItems.find((trip) => trip.id === this.tripId) || null;
    },
    tripHint() {
      if (!this.selectedTrip) return "";
      if (!this.selectedTrip.has_bus) return this.$t("assign_a_driver_and_bus_first");
      return "";
    },
    seatWarning() {
      const trip = this.selectedTrip;
      if (!trip) return "";
      if (!trip.has_bus) return this.$t("assign_a_driver_and_bus_first");
      if (trip.available_seats < this.students.length) {
        return this.$t("not_enough_seats", {
          seats: trip.available_seats,
          count: this.students.length,
        });
      }
      return "";
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        //a decision to notify belongs to one change, not to the screen
        this.notify = false;
        this.reason = "";
        this.loadOptions();
      }
    },
    pickUp() {
      //a route belongs to one direction, so the choice below it is no longer valid
      this.routeId = null;
      this.stopId = null;
      this.tripId = null;
    },
  },
  methods: {
    loadOptions() {
      this.isLoading = true;
      axios
        .get("/school-students/mapping-options")
        .then((response) => {
          this.routes = response.data.routes || [];
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_routes"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onRouteChange() {
      this.stopId = null;
      this.tripId = null;
    },
    save() {
      this.isSaving = true;
      axios
        .post("/school-students/map", {
          student_ids: this.students.map((student) => student.id),
          route_id: this.routeId,
          stop_id: this.stopId,
          trip_id: this.tripId,
          pick_up: this.pickUp,
          notify: this.notify,
          reason: this.reason,
        })
        .then((response) => {
          const failed = response.data.failed || [];
          this.$notifySuccess(
            this.$t("students_mapped", { count: (response.data.mapped || []).length })
          );
          if (failed.length) {
            this.$emit("partial-failure", failed);
          }
          this.open = false;
          //the mapping each child had a moment ago, so this can be taken back
          this.$emit("mapped", { snapshot: response.data.snapshot || [] });
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_mapping_students"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    clearMapping() {
      this.isSaving = true;
      axios
        .post("/school-students/clear-mapping", {
          student_ids: this.students.map((student) => student.id),
          pick_up: this.pickUp,
          notify: this.notify,
          reason: this.reason,
        })
        .then((response) => {
          this.$notifySuccess(
            this.$t("students_unmapped", { count: (response.data.cleared || []).length })
          );
          this.open = false;
          this.$emit("mapped", { snapshot: response.data.snapshot || [] });
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_mapping_students"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
